import { Box, Loader, Paper, Text, useMantineColorScheme } from "@mantine/core";
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "../../../hooks/useTranslation";
import { useFaqMarkdownContent } from "../hooks/useFaqMarkdownContent";
import { faqMarkdownComponents } from "../utils/faqMarkdownComponents";

interface FaqMarkdownPageProps {
    titleKey: string;
    titleFallback: string;
    markdownBasePath: string;
}

// Generic single-page FAQ topic shell: brand gradient header + the topic's editable
// Markdown content (see /public/locales/faq/*.md). Reused by every topic whose entire
// page is static prose (no live backend data) — Profile Completion has its own page
// since it also renders live rule data alongside its explanatory copy.
export const FaqMarkdownPage: React.FC<FaqMarkdownPageProps> = ({ titleKey, titleFallback, markdownBasePath }) => {
    const { t } = useTranslation();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === "dark";
    const { data: markdown, isLoading } = useFaqMarkdownContent(markdownBasePath);

    return (
        <Paper
            radius="md"
            withBorder={false}
            style={{ overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.10)" }}
        >
            <Box
                p="md"
                style={{
                    background: "linear-gradient(135deg, var(--mantine-color-brand-8) 0%, var(--mantine-color-brand-6) 100%)",
                }}
            >
                <Text fw={700} size="md" c="white">
                    {t(titleKey, undefined, titleFallback)}
                </Text>
            </Box>

            <Box p="lg" style={{ background: isDark ? "var(--mantine-color-dark-6)" : "white" }}>
                {isLoading && <Loader size="sm" />}
                {!isLoading && markdown && (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={faqMarkdownComponents}>
                        {markdown}
                    </ReactMarkdown>
                )}
            </Box>
        </Paper>
    );
};
